import type { BillType, SenateFloorVoteReference } from "./types";

const STATE_CODE_TO_NAME: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  PR: "Puerto Rico",
  GU: "Guam",
  VI: "Virgin Islands",
  MP: "Northern Mariana Islands",
  AS: "American Samoa",
};

const STATE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => [name.toLowerCase(), code]),
);

export function normalizeSearchText(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeCitation(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function buildBillId(congress: number, billType: string, billNumber: string | number): string {
  return `${congress}-${String(billType).toLowerCase()}-${String(billNumber)}`;
}

export function buildMeetingId(congress: number, chamber: string, eventId: string | number): string {
  return `${congress}-${String(chamber).toLowerCase()}-${String(eventId)}`;
}

export function buildBillCitation(billType: string, billNumber: string | number): string {
  return `${String(billType).toUpperCase()} ${String(billNumber)}`;
}

export function buildSenateFloorVoteReferenceFromIssue(issue: string, title: string | null = null): SenateFloorVoteReference {
  const value = issue.trim();
  const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");

  if (!value || normalized === "NA") {
    return { type: "proceeding", label: "Proceeding", value: null, title };
  }

  if (/^PN\d+/.test(normalized)) {
    return { type: "nomination", label: "Nomination number", value, title };
  }

  if (/^TREATYDOC/.test(normalized) || /^TD\d+/.test(normalized)) {
    return { type: "treaty", label: "Treaty document", value, title };
  }

  if (/^SAMDT\d+$/.test(normalized) || /^SA\d+$/.test(normalized)) {
    return { type: "amendment", label: "Amendment", value, title };
  }

  if (/^(HJRES|SJRES|HCONRES|SCONRES|HRES|SRES|HR|S)\d+$/.test(normalized)) {
    return { type: "measure", label: "Measure", value, title };
  }

  return { type: "unknown", label: "Reference", value, title };
}

export function resolveSenateFloorVoteReference(vote: {
  issue: string;
  reference?: SenateFloorVoteReference | null;
  title?: string | null;
}): SenateFloorVoteReference {
  if (vote.reference) {
    return vote.reference;
  }

  return buildSenateFloorVoteReferenceFromIssue(vote.issue, vote.title ?? null);
}

export function formatSenateFloorVoteReference(reference: SenateFloorVoteReference): string {
  return reference.value ? `${reference.label}: ${reference.value}` : reference.label;
}

export function formatSenateFloorVoteReferenceForVote(vote: {
  issue: string;
  reference?: SenateFloorVoteReference | null;
  title?: string | null;
}): string {
  return formatSenateFloorVoteReference(resolveSenateFloorVoteReference(vote));
}

export function toBillType(value: string): BillType {
  return value.toLowerCase() as BillType;
}

export function humanizeMemberChamber(chamber: string | null | undefined): "House" | "Senate" {
  return chamber === "Senate" ? "Senate" : "House";
}

export function toOriginChamber(chamber: string | null | undefined): "House" | "Senate" | null {
  if (!chamber) return null;
  if (chamber.toLowerCase().startsWith("house")) return "House";
  if (chamber.toLowerCase().startsWith("senate")) return "Senate";
  return null;
}

export function toMeetingChamber(chamber: string | null | undefined): "House" | "Senate" | "NoChamber" {
  if (chamber === "House") return "House";
  if (chamber === "Senate") return "Senate";
  return "NoChamber";
}

export function toPartyCode(partyName: string | null | undefined): string | null {
  if (!partyName) return null;
  const normalized = partyName.toLowerCase();
  if (normalized.startsWith("dem")) return "D";
  if (normalized.startsWith("rep")) return "R";
  if (normalized === "independent") return "I";
  if (normalized === "independent democrat") return "ID";
  if (normalized === "libertarian") return "L";
  return partyName.slice(0, 2).toUpperCase();
}

export function resolvePartyCode(partyCode: string | null | undefined, partyName: string | null | undefined): string | null {
  const normalizedCode = partyCode?.trim().toUpperCase();
  if (normalizedCode) return normalizedCode;
  return toPartyCode(partyName);
}

const MEMBER_NAME_SUFFIXES = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
const MEMBER_NAME_PREFIX_PATTERN =
  /^(resident commissioner|vice president|representative|senator|commissioner|chairman|chairwoman|delegate|speaker|governor|president|chair|rep\.?|sen\.?|del\.?|gov\.?|hon\.?|mr\.?|mrs\.?|ms\.?|dr\.?)\s+/i;
const MEMBER_NAME_METADATA_PATTERN = /(?:\s+(?:\[[^[\]]+\]|\([^()]+\)))+$/;

function splitMemberNamePrefix(segment: string): { prefix: string | null; lastName: string } {
  let remaining = segment.trim();
  const prefixParts: string[] = [];

  while (remaining) {
    const match = remaining.match(MEMBER_NAME_PREFIX_PATTERN);
    if (!match) break;

    prefixParts.push(match[1].trim());
    remaining = remaining.slice(match[0].length).trim();
  }

  return {
    prefix: prefixParts.length > 0 ? prefixParts.join(" ") : null,
    lastName: remaining,
  };
}

export function normalizeMemberDisplayName(name: string | null | undefined): string {
  if (!name) return "";
  const normalized = name.trim().replace(/\s+/g, " ");
  if (!normalized.includes(",")) return normalized;

  const metadataSuffix = normalized.match(MEMBER_NAME_METADATA_PATTERN)?.[0].trim() ?? "";
  const coreName = metadataSuffix ? normalized.slice(0, -metadataSuffix.length).trim() : normalized;
  const parts = coreName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) return normalized;

  const { prefix, lastName } = splitMemberNamePrefix(parts[0]);
  if (!lastName) return normalized;

  const trailingParts = parts.slice(1);
  const trailingSuffix = trailingParts.at(-1);
  const suffix = trailingSuffix && trailingParts.length > 1 && MEMBER_NAME_SUFFIXES.has(trailingSuffix.toLowerCase()) ? trailingSuffix : null;
  const givenNames = (suffix ? trailingParts.slice(0, -1) : trailingParts).join(" ");

  return [prefix, givenNames, lastName, suffix, metadataSuffix].filter(Boolean).join(" ").trim();
}

export function formatCommitteeDisplayName(name: string | null | undefined): string {
  const normalized = name?.trim().replace(/\s+/g, " ");
  if (!normalized) return "";

  return normalized.replace(/^Committee on the\s+/i, "").replace(/^Committee on\s+/i, "");
}

export function normalizeCommitteeRouteChamber(value: string | null | undefined): "house" | "senate" | "joint" | "" {
  switch ((value ?? "").trim().toLowerCase()) {
    case "house":
      return "house";
    case "senate":
      return "senate";
    case "joint":
      return "joint";
    default:
      return "";
  }
}

export function inferCommitteeRouteChamber(
  committeeCode: string | null | undefined,
  chamber: string | null | undefined,
): "house" | "senate" | "joint" | "" {
  const normalizedCode = committeeCode?.trim().toUpperCase() ?? "";
  if (normalizedCode.startsWith("J")) {
    return "joint";
  }
  if (normalizedCode.startsWith("SS")) {
    return "senate";
  }
  if (normalizedCode.startsWith("HS")) {
    return "house";
  }

  const normalizedChamber = normalizeCommitteeRouteChamber(chamber);
  if (normalizedChamber) {
    return normalizedChamber;
  }

  return "";
}

export function normalizeCommitteeCodeForMatching(code: string | null | undefined, chamber: string | null | undefined): string | null {
  const normalizedCode = code?.trim().toUpperCase();
  if (!normalizedCode) return null;

  if (chamber?.trim().toLowerCase() === "house" && normalizedCode.startsWith("H") && normalizedCode.length > 2) {
    return normalizedCode.slice(2);
  }

  return normalizedCode;
}

export function normalizeMemberPortraitUrl(portraitUrl: string | null | undefined): string | null {
  const trimmed = portraitUrl?.trim();
  if (!trimmed) return null;

  const congressPrefix = "https://www.congress.gov/img/member/";
  const bioguidePrefix = "https://bioguide.congress.gov";
  const normalized = trimmed.startsWith(congressPrefix) ? trimmed.slice(congressPrefix.length) : trimmed;

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }
  if (normalized.startsWith("/img/member/")) {
    return `https://www.congress.gov${normalized}`;
  }
  if (normalized.startsWith("/photo/")) {
    return `${bioguidePrefix}${normalized}`;
  }

  return `${congressPrefix}${normalized.replace(/^\/+/, "")}`;
}

export function normalizeStateCode(state: string | null | undefined): string | null {
  if (!state) return null;
  const trimmed = state.trim();
  if (!trimmed) return null;

  const uppercase = trimmed.toUpperCase();
  if (STATE_CODE_TO_NAME[uppercase]) return uppercase;

  const normalizedName = trimmed
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");

  return STATE_NAME_TO_CODE[normalizedName] ?? uppercase;
}

export function stateNameFromCode(state: string | null | undefined): string | null {
  const normalizedCode = normalizeStateCode(state);
  return normalizedCode ? STATE_CODE_TO_NAME[normalizedCode] ?? null : null;
}

export function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function dedupe<T>(items: T[]): T[] {
  return [...new Set(items)];
}

export function isStaleTimestamp(value: string | null, thresholdMinutes = 30): boolean {
  if (!value) return true;
  const time = Date.parse(value);
  if (Number.isNaN(time)) return true;
  return Date.now() - time > thresholdMinutes * 60 * 1000;
}

export function isoNow(): string {
  return new Date().toISOString();
}

export function subtractMinutes(timestamp: string | null, minutes: number): string | null {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  date.setMinutes(date.getMinutes() - minutes);
  return date.toISOString();
}

export function toCongressApiDateTime(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp for Congress API: ${timestamp}`);
  }
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}
